import { useState, useEffect } from 'react';
import { adminAPI } from '../../lib/api';
import AdminLayout from '../../components/AdminLayout';
import { Badge } from '../../components/ui/badge';
import { Users, Terminal, DollarSign, Bot, TrendingUp, Activity, ArrowUpRight, ArrowDownRight } from 'lucide-react';
import { motion } from 'framer-motion';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, AreaChart, Area } from 'recharts';

const StatCard = ({ icon: Icon, label, value, sub, color, delay }) => (
  <motion.div
    initial={{ opacity: 0, y: 10 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ delay }}
    className="card-cyber p-5 rounded-sm"
  >
    <div className="flex items-center justify-between mb-3">
      <Icon className="w-5 h-5" style={{ color }} />
      <Badge className="font-mono text-[10px] uppercase" style={{ backgroundColor: `${color}15`, color, borderColor: `${color}30` }}>
        {sub}
      </Badge>
    </div>
    <p className="font-heading text-3xl font-bold text-white">{value}</p>
    <p className="font-mono text-[10px] text-[#aaa] mt-1 tracking-widest">{label}</p>
  </motion.div>
);

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-[#111] border border-white/10 rounded-sm p-3 font-mono text-xs">
      <p className="text-[#888] mb-1">{label}</p>
      {payload.map((p, i) => (
        <p key={i} style={{ color: p.color }}>{p.name}: {typeof p.value === 'number' && p.name.includes('$') ? `$${p.value.toFixed(2)}` : p.value}</p>
      ))}
    </div>
  );
};

export default function AdminDashboard() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    adminAPI.stats().then(res => {
      setStats(res.data);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-[60vh]">
          <div className="w-8 h-8 border-2 border-[#FF7A3D] border-t-transparent rounded-full animate-spin" />
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div data-testid="admin-dashboard">
        <div className="mb-8">
          <p className="font-mono text-xs text-[#ff003c] tracking-widest uppercase mb-2">SUPER ADMIN</p>
          <h1 className="font-heading text-3xl font-bold text-white tracking-tight">Platform Command Center</h1>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <StatCard icon={Users} label="TOTAL USERS" value={stats?.total_users || 0} sub="USERS" color="#FF7A3D" delay={0.05} />
          <StatCard icon={DollarSign} label="TOTAL REVENUE" value={`$${(stats?.total_revenue || 0).toFixed(2)}`} sub="REVENUE" color="#00E5C1" delay={0.1} />
          <StatCard icon={Terminal} label="TOTAL EXECUTIONS" value={stats?.total_executions || 0} sub="RUNS" color="#ffbd2e" delay={0.15} />
          <StatCard icon={Bot} label="ACTIVE AGENTS" value={`${stats?.active_agents || 0}/${stats?.total_agents || 18}`} sub="AGENTS" color="#ff003c" delay={0.2} />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-8">
          {/* Executions Chart */}
          <div className="card-cyber p-5 rounded-sm" data-testid="executions-chart">
            <h2 className="font-heading text-sm font-bold text-white mb-4">DAILY EXECUTIONS</h2>
            {stats?.daily_executions?.length > 0 ? (
              <ResponsiveContainer width="100%" height={200}>
                <AreaChart data={[...stats.daily_executions].reverse()}>
                  <defs>
                    <linearGradient id="gGreen" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#FF7A3D" stopOpacity={0.3} />
                      <stop offset="95%" stopColor="#FF7A3D" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <XAxis dataKey="date" tick={{ fill: '#555', fontSize: 10, fontFamily: 'JetBrains Mono' }} axisLine={{ stroke: '#222' }} tickLine={false} />
                  <YAxis tick={{ fill: '#555', fontSize: 10, fontFamily: 'JetBrains Mono' }} axisLine={{ stroke: '#222' }} tickLine={false} />
                  <Tooltip content={<CustomTooltip />} />
                  <Area type="monotone" dataKey="count" stroke="#FF7A3D" fill="url(#gGreen)" strokeWidth={2} name="Executions" />
                </AreaChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-[200px] flex items-center justify-center">
                <p className="font-mono text-xs text-[#aaa]">No data yet</p>
              </div>
            )}
          </div>

          {/* Revenue Chart */}
          <div className="card-cyber p-5 rounded-sm" data-testid="revenue-chart">
            <h2 className="font-heading text-sm font-bold text-white mb-4">DAILY REVENUE</h2>
            {stats?.daily_revenue?.length > 0 ? (
              <ResponsiveContainer width="100%" height={200}>
                <BarChart data={[...stats.daily_revenue].reverse()}>
                  <XAxis dataKey="date" tick={{ fill: '#555', fontSize: 10, fontFamily: 'JetBrains Mono' }} axisLine={{ stroke: '#222' }} tickLine={false} />
                  <YAxis tick={{ fill: '#555', fontSize: 10, fontFamily: 'JetBrains Mono' }} axisLine={{ stroke: '#222' }} tickLine={false} />
                  <Tooltip content={<CustomTooltip />} />
                  <Bar dataKey="amount" fill="#00E5C1" radius={[2, 2, 0, 0]} name="$ Revenue" />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-[200px] flex items-center justify-center">
                <p className="font-mono text-xs text-[#aaa]">No data yet</p>
              </div>
            )}
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {/* Top Agents */}
          <div className="card-cyber p-5 rounded-sm" data-testid="top-agents">
            <h2 className="font-heading text-sm font-bold text-white mb-4">TOP AGENTS BY USAGE</h2>
            {stats?.top_agents?.length > 0 ? (
              <div className="space-y-2">
                {stats.top_agents.map((a, i) => (
                  <div key={a.agent_id} className="flex items-center justify-between p-2 bg-white/[0.02] border border-white/5 rounded-sm">
                    <div className="flex items-center gap-3">
                      <span className="font-mono text-xs text-[#aaa] w-5">{String(i + 1).padStart(2, '0')}</span>
                      <span className="font-mono text-xs text-white">{a.agent_id}</span>
                    </div>
                    <div className="flex items-center gap-4">
                      <span className="font-mono text-xs text-[#FF7A3D]">{a.count} runs</span>
                      <span className="font-mono text-xs text-[#888]">{a.credits.toFixed(2)} cr</span>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="font-mono text-xs text-[#aaa]">No execution data yet</p>
            )}
          </div>

          {/* Recent Users */}
          <div className="card-cyber p-5 rounded-sm" data-testid="recent-users">
            <h2 className="font-heading text-sm font-bold text-white mb-4">RECENT REGISTRATIONS</h2>
            {stats?.recent_users?.length > 0 ? (
              <div className="space-y-2">
                {stats.recent_users.map(u => (
                  <div key={u.user_id} className="flex items-center justify-between p-2 bg-white/[0.02] border border-white/5 rounded-sm">
                    <div>
                      <p className="font-mono text-xs text-white">{u.name || u.email}</p>
                      <p className="font-mono text-[10px] text-[#aaa]">{u.email}</p>
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge className="bg-[#FF7A3D]/10 text-[#FF7A3D] border-[#FF7A3D]/20 font-mono text-[10px]">{u.plan || 'free'}</Badge>
                      <span className="font-mono text-[10px] text-[#888]">{(u.credits || 0).toFixed(1)} cr</span>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="font-mono text-xs text-[#aaa]">No users yet</p>
            )}
          </div>
        </div>
      </div>
    </AdminLayout>
  );
}
